import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';
import SummaryCards from './components/SummaryCards';
import PerfDailyTrendChart from './components/PerfDailyTrendChart';
import ManagerComparisonChart from './components/ManagerComparisonChart';
import ServerObjectivesSection from './components/ServerObjectivesSection';
import ManagerObjectivesTable from './components/ManagerObjectivesTable';
import CreatorCurrentTable from './components/CreatorCurrentTable';
import ObjectifsADateSection from './components/ObjectifsADateSection';
import OpportunitesBonusSection from './components/OpportunitesBonusSection';

const FALLBACK_PALIERS = [
  { pct: 70, bonus: 2 },
  { pct: 75, bonus: 3 },
  { pct: 80, bonus: 4 },
  { pct: 85, bonus: 5 },
  { pct: 90, bonus: 7 },
  { pct: 95, bonus: 8 },
  { pct: 100, bonus: 9 },
  { pct: 105, bonus: 10 },
  { pct: 110, bonus: 11 },
  { pct: 115, bonus: 12 },
  { pct: 120, bonus: 13 },
  { pct: 125, bonus: 14 },
  { pct: 130, bonus: 15 },
];

const showToast = (msg, type = 'error') => {
  const el = document.createElement('div');
  el.textContent = msg;
  el.style.cssText = `
    position:fixed;bottom:24px;right:24px;z-index:9999;
    padding:12px 20px;border-radius:10px;font-size:13px;font-family:Inter,sans-serif;
    background:${type === 'error' ? '#1a0000' : '#001a00'};
    color:${type === 'error' ? '#ff4444' : '#22c55e'};
    border:1px solid ${type === 'error' ? 'rgba(229,32,42,0.3)' : 'rgba(34,197,94,0.3)'};
    box-shadow:0 4px 20px rgba(0,0,0,0.5);
  `;
  document.body?.appendChild(el);
  setTimeout(() => el?.remove(), 4000);
};

const SectionHeader = ({ color, label }) => (
  <div className="flex items-center gap-2 mb-4">
    <div className="w-1 h-4 rounded-full" style={{ backgroundColor: color }} />
    <h2
      className="text-xs font-semibold uppercase tracking-widest"
      style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}
    >
      {label}
    </h2>
  </div>
);

const PerformanceActuelle = () => {
  const { t } = useTranslation();
  const currentMonthCap = t(`months.${new Date().getMonth()}`);

  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // ── State ──────────────────────────────────────────────────────────────────
  const [currentPeriod, setCurrentPeriod] = useState('');
  const [serversData, setServersData] = useState([]);
  const [managers, setManagers] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [incrementalConfig, setIncrementalConfig] = useState([]);
  const [managerObjectives, setManagerObjectives] = useState([]);
  const [dailyTrendData, setDailyTrendData] = useState([]);
  const [lastDailyDate, setLastDailyDate] = useState('');

  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);

  // ── Filters ────────────────────────────────────────────────────────────────
  const [selectedServer, setSelectedServer] = useState('Tous');
  const [selectedManagerId, setSelectedManagerId] = useState(searchParams?.get('manager_id') || '');
  const [selectedLevel, setSelectedLevel] = useState('');

  useEffect(() => {
    const serverParam = searchParams?.get('server');
    if (serverParam && serverParam !== selectedServer) {
      setSelectedServer(serverParam);
    }
  }, []);

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (session?.user?.email) setUserEmail(session?.user?.email);
    });
  }, []);

  // ── PART 1: Fetch dynamic period ───────────────────────────────────────────
  const fetchPeriod = useCallback(async () => {
    try {
      const { data: periodData, error } = await supabase?.from('creator_current_month')?.select('period')?.order('updated_at', { ascending: false })?.limit(1)?.single();

      if (error && error?.code !== 'PGRST116') {
        console.error('Period fetch error:', error);
      }

      const period =
        periodData?.period ||
        `${new Date()?.getFullYear()}-${String(new Date()?.getMonth() + 1)?.padStart(2, '0')}`;
      return period;
    } catch (err) {
      console.error('Period fetch error:', err);
      return `${new Date()?.getFullYear()}-${String(new Date()?.getMonth() + 1)?.padStart(2, '0')}`;
    }
  }, []);

  // ── PART 2: Fetch servers + incremental_config ─────────────────────────────
  const fetchConfig = useCallback(async (period) => {
    setConfigLoading(true);
    try {
      const [serversRes, incrementalRes] = await Promise.all([
        supabase?.from('servers')?.select('id, name, color')?.eq('is_active', true),
        supabase?.from('incremental_config')?.select('server, target_100pct, completion_tiers')?.eq('period', period),
      ]);

      if (serversRes?.error) showToast(t('admin.performance.errorLoadingServers'));
      else setServersData(serversRes?.data || []);

      if (incrementalRes?.error) showToast(t('admin.performance.errorLoadingConfig'));
      else setIncrementalConfig(incrementalRes?.data || []);
    } catch (err) {
      showToast(t('admin.performance.errorLoadingConfig'));
    } finally {
      setConfigLoading(false);
    }
  }, [t]);

  // ── Fetch managers ─────────────────────────────────────────────────────────
  const fetchManagers = useCallback(async () => {
    try {
      const { data, error } = await supabase?.from('users')?.select('id, name')?.in('role', ['manager', 'region_manager'])?.order('name');
      if (error) showToast(t('admin.performance.errorLoadingManagers'));
      else setManagers(data || []);
    } catch (err) {
      showToast(t('admin.performance.errorLoadingManagers'));
    }
  }, [t]);

  // ── PART 3: Fetch main data from creator_current_month ────────────────────
  const fetchCurrentData = useCallback(async (period, server, managerId) => {
    setLoading(true);
    try {
      let query = supabase?.from('creator_current_month')?.select('tiktok_id, username, server, manager_name, manager_id, diamonds, tier_current, tier_m1, activeness_level, bonus_rankup, bonus_activeness, is_active, is_new_recruit, cashback_amount, cashback_pct, period, days_elapsed, updated_at, tier_status, live_days, live_duration_minutes, objective_diamonds, gap_to_objective, rankup_eligible, projected_diamonds')?.eq('period', period);

      if (server !== 'Tous') query = query?.eq('server', server);
      if (managerId) query = query?.eq('manager_id', managerId);

      const { data, error } = await query;
      if (error) {
        showToast(t('admin.performance.errorLoadingCreators'));
        setCurrentData([]);
      } else {
        // Enrichir avec level + display_name depuis creators
        const tiktokIds = (data || [])?.map(r => r?.tiktok_id)?.filter(Boolean);
        let creatorsMap = {};
        if (tiktokIds?.length > 0) {
          const { data: creatorsData } = await supabase?.from('creators')?.select('tiktok_id, level, display_name')?.in('tiktok_id', tiktokIds);
          creatorsData?.forEach(c => { creatorsMap[c?.tiktok_id] = c; });
        }
        setCurrentData((data || [])?.map(r => ({
          ...r,
          level: creatorsMap[r?.tiktok_id]?.level || null,
          display_name: creatorsMap[r?.tiktok_id]?.display_name || null,
        })));
      }
    } catch (err) {
      showToast(t('admin.performance.errorLoadingCreators'));
      setCurrentData([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // ── PART 7: Fetch manager objectives ──────────────────────────────────────
  const fetchManagerObjectives = useCallback(async (period) => {
    try {
      const { data, error } = await supabase?.from('manager_objectives')?.select(`
          manager_id,
          server,
          objective_diamonds,
          diamonds_m1,
          users!manager_objectives_manager_id_fkey(id, name)
        `)?.eq('period', period);
      if (error) showToast(t('admin.performance.errorLoadingObjectives'));
      else setManagerObjectives(data || []);
    } catch (err) {
      showToast(t('admin.performance.errorLoadingObjectives'));
    }
  }, [t]);

  // ── PART 8: Fetch daily trend from creator_daily with pagination ──────────
  const fetchDailyTrend = useCallback(async (period, server) => {
    try {
      let allDailyTrend = [];
      let page = 0;
      while (true) {
        // Compute last day of month (handles Feb, Apr, Jun, Sep, Nov correctly)
        const [pY, pM] = period.split('-').map(Number);
        const lastDay = new Date(pY, pM, 0).getDate();
        let query = supabase?.from('creator_daily')
          ?.select('date, diamonds, server, live_streams')
          ?.gte('date', `${period}-01`)
          ?.lte('date', `${period}-${String(lastDay).padStart(2, '0')}`)
          ?.order('date')
          ?.range(page * 1000, (page + 1) * 1000 - 1);

        const { data, error } = await query;
        if (error) { showToast(t('admin.performance.errorLoadingDailyTrend')); break; }
        if (!data || data?.length === 0) break;
        allDailyTrend = [...allDailyTrend, ...data];
        if (data?.length < 1000) break;
        page++;
      }

      // Filter by server before aggregating
      const filteredTrend = server !== 'Tous'
        ? allDailyTrend?.filter((r) => r?.server === server)
        : allDailyTrend;

      // Aggregate by date
      const trendByDate = {};
      filteredTrend?.forEach((r) => {
        if (!trendByDate?.[r?.date])
          trendByDate[r.date] = { date: r?.date, diamonds: 0, live_streams: 0 };
        trendByDate[r.date].diamonds += r?.diamonds || 0;
        trendByDate[r.date].live_streams += r?.live_streams || 0;
      });
      const trendData = Object.values(trendByDate)
        ?.sort((a, b) => a?.date?.localeCompare(b?.date));

      setDailyTrendData(trendData);
    } catch (err) {
      showToast(t('admin.performance.errorLoadingDailyTrend'));
    }
  }, [t]);

  // ── Bootstrap: fetch period first, then everything else ───────────────────
  useEffect(() => {
    const init = async () => {
      const period = await fetchPeriod();
      setCurrentPeriod(period);
      await Promise.all([
        fetchConfig(period),
        fetchManagers(),
        fetchManagerObjectives(period),
      ]);
    };
    init();
  }, [fetchPeriod, fetchConfig, fetchManagers, fetchManagerObjectives]);

  // ── Re-fetch data when filters or period change ────────────────────────────
  useEffect(() => {
    if (!currentPeriod) return;
    fetchCurrentData(currentPeriod, selectedServer, selectedManagerId);
    fetchDailyTrend(currentPeriod, selectedServer);
  }, [currentPeriod, selectedServer, selectedManagerId, fetchCurrentData, fetchDailyTrend]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleServerChange = (server) => {
    setSelectedServer(server);
    const params = {};
    if (server !== 'Tous') params.server = server;
    if (selectedManagerId) params.manager_id = selectedManagerId;
    setSearchParams(params);
  };

  const handleManagerChange = (managerId) => {
    setSelectedManagerId(managerId);
    const params = {};
    if (selectedServer !== 'Tous') params.server = selectedServer;
    if (managerId) params.manager_id = managerId;
    setSearchParams(params);
  };

  // ── Derived: targets map from incremental_config ──────────────────────────
  const targets = useMemo(() => {
    const map = {};
    incrementalConfig?.forEach((r) => {
      if (r?.server) map[r.server] = r?.target_100pct || 0;
    });
    return map;
  }, [incrementalConfig]);

  // ── Derived: paliers per server from incremental_config ───────────────────
  const paliersMap = useMemo(() => {
    const map = {};
    incrementalConfig?.forEach((r) => {
      if (r?.server && Array.isArray(r?.completion_tiers) && r?.completion_tiers?.length > 0) {
        map[r.server] = r?.completion_tiers;
      }
    });
    return map;
  }, [incrementalConfig]);

  // ── Derived: level-filtered data (client-side) ─────────────────────────────
  const filteredData = useMemo(() => {
    if (!selectedLevel) return currentData;
    return currentData?.filter((r) => r?.level === selectedLevel);
  }, [currentData, selectedLevel]);

  // ── PART 4: Summary ────────────────────────────────────────────────────────
  const fetchLastDailyDate = useCallback(async () => {
    try {
      const { data } = await supabase?.from('creator_daily')
        ?.select('date')
        ?.order('date', { ascending: false })
        ?.limit(1)
        ?.single();
      if (data?.date) setLastDailyDate(data?.date);
    } catch (err) {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchLastDailyDate();
  }, [fetchLastDailyDate]);

  const summary = useMemo(() => {
    const total_diamonds = filteredData?.reduce((sum, r) => sum + (r?.diamonds || 0), 0);
    const total_creators = filteredData?.length;
    const active_creators = filteredData?.filter((r) => r?.is_active === true)?.length;
    const go_live_rate = total_creators > 0 ? Math.round((active_creators / total_creators) * 100) : 0;
    const days_elapsed = filteredData?.reduce((max, r) => Math.max(max, r?.days_elapsed || 0), 0);
    const new_recruits = filteredData?.filter((r) => r?.is_new_recruit === true)?.length || 0;
    return { total_diamonds, total_creators, active_creators, go_live_rate, days_elapsed, last_update: lastDailyDate, new_recruits };
  }, [filteredData, lastDailyDate]);

  // ── PART 9: Manager comparison data ───────────────────────────────────────
  const managerPerfData = useMemo(() => {
    const managerPerf = {};
    filteredData?.forEach((r) => {
      const key = r?.manager_id || 'unassigned';
      if (!managerPerf?.[key]) {
        managerPerf[key] = {
          name: r?.manager_name || t('common.unassigned'),
          diamonds: 0,
          active_creators: 0,
          bonus_total: 0,
        };
      }
      managerPerf[key].diamonds += r?.diamonds || 0;
      managerPerf[key].bonus_total += (r?.bonus_rankup || 0) + (r?.bonus_activeness || 0);
      if (r?.is_active) managerPerf[key].active_creators++;
    });
    return Object.values(managerPerf)?.filter((m) => m?.diamonds > 0)?.sort((a, b) => b?.diamonds - a?.diamonds);
  }, [filteredData]);

  // ── Server color helper ────────────────────────────────────────────────────
  const getServerColor = useCallback(
    (serverId) => {
      const srv = serversData?.find((s) => s?.id === serverId);
      return srv?.color || '#E5202A';
    },
    [serversData]
  );

  const serverColor =
    selectedServer === 'Tous' ?'#E5202A'
      : getServerColor(selectedServer);

  // ── Period display ─────────────────────────────────────────────────────────
  const periodDisplay = useMemo(() => {
    if (!currentPeriod) return '—';
    const [year, month] = currentPeriod?.split('-');
    const monthIdx = parseInt(month, 10) - 1;
    return `${t(`months.${monthIdx}`, month)} ${year}`;
  }, [currentPeriod]);

  const serverTabs = useMemo(
    () => ['Tous', ...(serversData?.map((s) => s?.id) || [])],
    [serversData]
  );

  const selectedManagerName = useMemo(() => {
    if (!selectedManagerId) return null;
    return managers?.find((m) => m?.id === selectedManagerId)?.name || null;
  }, [selectedManagerId, managers]);

  return (
    <div
      className="flex min-h-screen"
      style={{ backgroundColor: 'var(--color-background)', fontFamily: 'Inter, sans-serif' }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <div className="hidden md:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((v) => !v)}
          userEmail={userEmail}
        />
      </div>
      <main className="flex-1 overflow-y-auto min-w-0 pb-16 md:pb-0">
        <AdminMobileHeader title={`${t('admin.performance.title', 'Performance')} ${currentMonthCap}`} />
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b"
          style={{ backgroundColor: 'var(--color-background)', borderColor: '#1f1f1f' }}
        >
          <div>
            <h1
              className="text-lg font-bold text-white"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {`${t('admin.performance.title', 'Performance')} ${currentMonthCap}`}
            </h1>
            <p
              className="text-xs"
              style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}
            >
              {selectedManagerName
                ? `${periodDisplay} — ${selectedManagerName}`
                : `${periodDisplay} — ${t('admin.performance.realtimeData', 'données en temps réel')}`}
            </p>
          </div>
          <div
            className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
            style={{
              backgroundColor: 'rgba(229,32,42,0.08)',
              borderColor: 'rgba(229,32,42,0.2)',
              color: '#E5202A',
              fontFamily: 'Sora, sans-serif',
            }}
          >
            {periodDisplay}
          </div>
        </div>

        {/* Filter Bar 1 — Server Tabs */}
        <div
          className="px-6 py-3 border-b flex items-center gap-2 flex-wrap"
          style={{ borderColor: '#1f1f1f', backgroundColor: '#0a0a0a' }}
        >
          <span
            className="text-xs font-medium mr-2"
            style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}
          >
            {t('admin.performance.serverLabel', 'Serveur:')}
          </span>
          {serverTabs?.map((srv) => {
            const isActive = selectedServer === srv;
            const color = srv === 'Tous' ? '#E5202A' : getServerColor(srv);
            return (
              <button
                key={srv}
                onClick={() => handleServerChange(srv)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold tracking-wider transition-all"
                style={{
                  backgroundColor: isActive ? `${color}20` : 'transparent',
                  color: isActive ? color : 'rgba(255,255,255,0.4)',
                  border: `1px solid ${isActive ? color + '50' : '#2a2a2a'}`,
                  fontFamily: 'Sora, sans-serif',
                }}
              >
                {srv}
              </button>
            );
          })}
        </div>

        {/* Filter Bar 2 — Manager + Level */}
        <div
          className="px-6 py-3 border-b flex items-center gap-4 flex-wrap"
          style={{ borderColor: '#1f1f1f', backgroundColor: '#0a0a0a' }}
        >
          {/* Level filter */}
          <div className="flex items-center gap-2">
            <span
              className="text-xs font-medium"
              style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}
            >
              {t('admin.performance.levelLabel', 'Niveau:')}
            </span>
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e?.target?.value)}
              className="rounded-lg px-3 py-1.5 text-base outline-none"
              style={{
                backgroundColor: '#1a1a1a',
                border: `1px solid ${selectedLevel ? '#E5202A50' : '#2a2a2a'}`,
                color: selectedLevel ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)',
                fontFamily: 'Inter, sans-serif',
                minWidth: '140px',
              }}
            >
              <option value="">{t('admin.performance.allLevels', 'Tous les niveaux')}</option>
              <option value="Junior">Junior</option>
              <option value="Rising">Rising</option>
              <option value="Senior">Senior</option>
              <option value="Elite">Elite</option>
            </select>
            {selectedLevel && (
              <button
                onClick={() => setSelectedLevel('')}
                className="text-xs px-2 py-1 rounded"
                style={{ color: 'rgba(255,255,255,0.4)', backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a' }}
              >
                ✕
              </button>
            )}
          </div>

          <div style={{ width: '1px', height: '20px', backgroundColor: '#2a2a2a' }} />

          {/* Manager filter */}
          <div className="flex items-center gap-2">
            <span
              className="text-xs font-medium"
              style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}
            >
              {t('admin.performance.managerLabel', 'Manager:')}
            </span>
            <select
              value={selectedManagerId}
              onChange={(e) => handleManagerChange(e?.target?.value)}
              className="rounded-lg px-3 py-1.5 text-base outline-none"
              style={{
                backgroundColor: '#1a1a1a',
                border: `1px solid ${selectedManagerId ? '#E5202A50' : '#2a2a2a'}`,
                color: selectedManagerId ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)',
                fontFamily: 'Inter, sans-serif',
                minWidth: '180px',
              }}
            >
              <option value="">{t('admin.performance.allManagers', 'Tous les managers')}</option>
              {managers?.map((m) => (
                <option key={m?.id} value={m?.id}>
                  {m?.name}
                </option>
              ))}
            </select>
            {selectedManagerId && (
              <button
                onClick={() => handleManagerChange('')}
                className="text-xs px-2 py-1 rounded"
                style={{ color: 'rgba(255,255,255,0.4)', backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a' }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* SECTION 1 — Summary Cards */}
          <section>
            <SectionHeader color={serverColor} label={`${t('admin.performance.summary', 'Résumé')} — ${periodDisplay}`} />
            <SummaryCards summary={summary} serverColor={serverColor} loading={loading} />
            {/* Nouveaux recrutés KPI */}
            <div className="mt-3 flex gap-3 flex-wrap">
              <div
                className="rounded-xl p-3 border inline-flex flex-col"
                style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', minWidth: '160px' }}
              >
                <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
                  {t('admin.performance.newRecruits', 'Nouveaux recrutés')}
                </p>
                {loading ? (
                  <div className="h-5 w-12 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f' }} />
                ) : (
                  <p className="text-lg font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>
                    {summary?.new_recruits}
                  </p>
                )}
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>
                  {t('admin.performance.contractsThisMonth', 'contrats ce mois')}
                </p>
              </div>
              <div
                className="rounded-xl p-3 border inline-flex flex-col"
                style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', minWidth: '160px' }}
              >
                <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
                  {t('admin.performance.estimatedCashback', 'Cashback estimé')}
                </p>
                {loading ? (
                  <div className="h-5 w-12 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f' }} />
                ) : (
                  <p className="text-lg font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>
                    ${filteredData?.reduce((sum, r) => sum + (parseFloat(r?.cashback_amount) || 0), 0)?.toFixed(2)}
                  </p>
                )}
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>
                  {t('admin.performance.currentMonth', 'mois en cours')}
                </p>
              </div>
            </div>
          </section>

          {/* SECTION 2 — Objectif Incremental par Serveur */}
          <section>
            <SectionHeader color={serverColor} label={`${t('admin.performance.incrementalObjective', 'Objectif Incremental par Serveur')} — ${periodDisplay}`} />
            <ServerObjectivesSection
              serversData={serversData}
              selectedServer={selectedServer}
              currentData={filteredData}
              targets={targets}
              paliersMap={paliersMap}
              fallbackPaliers={FALLBACK_PALIERS}
              loading={loading || configLoading}
            />
          </section>

          {/* SECTION 3 — Objectifs À Date */}
          <section>
            <SectionHeader color="#E5202A" label={`${t('admin.performance.objectivesToDate', 'Objectifs à date')} — ${periodDisplay}`} />
            <ObjectifsADateSection
              currentData={filteredData}
              serversData={serversData}
              selectedServer={selectedServer}
              targets={targets}
              incrementalConfig={incrementalConfig}
              fallbackPaliers={FALLBACK_PALIERS}
              loading={loading}
            />
          </section>

          {/* SECTION 4 — Objectifs Managers */}
          <section>
            <SectionHeader color="#a855f7" label={`${t('admin.performance.managerObjectives', 'Objectifs Managers')} — ${periodDisplay}`} />
            <ManagerObjectivesTable
              managerObjectives={managerObjectives}
              currentData={filteredData}
              serversData={serversData}
              selectedServer={selectedServer}
              loading={loading || configLoading}
            />
          </section>

          {/* SECTION 5 — Tendance Journalière */}
          <section>
            <SectionHeader color={serverColor} label={t('admin.performance.dailyTrend', 'Tendance Journalière — Diamonds')} />
            <PerfDailyTrendChart
              data={dailyTrendData}
              serverColor={serverColor}
              loading={loading}
            />
          </section>

          {/* SECTION 6 — Comparaison Managers */}
          <section>
            <SectionHeader color={serverColor} label={t('admin.performance.managerComparison', 'Comparaison par Manager')} />
            <ManagerComparisonChart
              managerPerfData={managerPerfData}
              serverColor={serverColor}
              loading={loading}
            />
          </section>

          {/* SECTION 7 — Opportunités Bonus */}
          <section>
            <SectionHeader color={serverColor} label={t('admin.performance.bonusOpportunities', 'Opportunites Bonus')} />
            <OpportunitesBonusSection
              data={filteredData}
              loading={loading}
              serverColor={serverColor}
              basePath="/admin/creators"
            />
          </section>

          {/* SECTION 8 — Table Créateurs */}
          <section>
            <SectionHeader color={serverColor} label={t('admin.performance.creatorPerformance', 'Performance Créateurs')} />
            <CreatorCurrentTable
              data={filteredData}
              loading={loading}
              serverColor={serverColor}
              getServerColor={getServerColor}
            />
          </section>
        </div>
      </main>
      <AdminBottomNav />
    </div>
  );
};

export default PerformanceActuelle;
